import { useState } from 'react';
import { motion } from 'framer-motion';
import { bzTelecomData } from '../data/bzTelecomData';
import AikupCard from '../components/AikupCard';
import AikupModal from '../components/AikupModal';
import ExpertVisionModal from '../components/ExpertVisionModal';
import BZTelecomModal from '../components/BZTelecomModal';
import GuideVenteBZTelecomModal from '../components/GuideVenteBZTelecomModal';
import GouvernanceBZTelecomModal from '../components/GouvernanceBZTelecomModal';
import ComiteTIPrismeModal from '../components/ComiteTIPrismeModal';
import ResponsabiliteVentesBZTelecomModal from '../components/ResponsabiliteVentesBZTelecomModal';
import AIInsightBubble from '../components/AIInsightBubble';
import Header from '../layout/Header';
import LeadersSection from '../components/LeadersSection';
import FormationGestionDocumentaireCard from '../components/FormationGestionDocumentaireCard';
import FormationTelephonieBZTelecomModal from '../components/FormationTelephonieBZTelecomModal';
import ArbrePRIsMECard from '../components/ArbrePRIsMECard';
import FelixActionPlanModal from '../components/FelixActionPlanModal';
import type { BZTelecomItem } from '../data/bzTelecomData';
import { useNavigate } from 'react-router-dom';
import { BookOpenCheck, FileText, PhoneCall } from 'lucide-react';

export default function BZTelecom() {
  const navigate = useNavigate();
  const [selectedItem, setSelectedItem] = useState<BZTelecomItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBZTelecomModalOpen, setIsBZTelecomModalOpen] = useState(false);
  const [isGouvernanceModalOpen, setIsGouvernanceModalOpen] = useState(false);
  const [isReunionBZTelecomOpen, setIsReunionBZTelecomOpen] = useState(false);
  const [isGuideVenteModalOpen, setIsGuideVenteModalOpen] = useState(false);
  const [isFelixActionPlanModalOpen, setIsFelixActionPlanModalOpen] = useState(false);
  const [isResponsabiliteVentesOpen, setIsResponsabiliteVentesOpen] = useState(false);
  const [isBZTelecomTrainingOpen, setIsBZTelecomTrainingOpen] = useState(false);
  const [expertVisionModal, setExpertVisionModal] = useState<{
    isOpen: boolean;
    expert: 'dany' | 'yannick' | 'francois' | null;
    item: BZTelecomItem | null;
  }>({
    isOpen: false,
    expert: null,
    item: null,
  });

  const handleCardClick = (item: BZTelecomItem) => {
    if (item.id === 18) {
      // Partenariat Stratégique - Ouvrir le gros modal BZ Telecom
      setIsBZTelecomModalOpen(true);
    } else if (item.id === 19) {
      // Guide de Vente BZ Telecom - Ouvrir le guide du parfait vendeur
      setIsGuideVenteModalOpen(true);
    } else if (item.id === 20) {
      // Plan d'action Felix (Avril–mai 2026)
      setIsFelixActionPlanModalOpen(true);
    } else if (item.id === 7) {
      // Gouvernance & RACI - Ouvrir le modal Gouvernance BZ Telecom
      setIsGouvernanceModalOpen(true);
    } else if (item.id === 15) {
      // Comité BZ Telecom - Réunions de gouvernance
      setIsReunionBZTelecomOpen(true);
    } else if (item.id === 21) {
      // Responsabilités ventes BZ Telecom
      setIsResponsabiliteVentesOpen(true);
    } else {
      setSelectedItem(item);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedItem(null), 300);
  };

  const leaders = [
    {
      name: "Martin Carrier",
      title: "Président BZ Telecom",
      avatar: "/MC.png",
      vision: "Martin Carrier est le président de BZ Telecom, compagnie sœur de BZ Inc. Visionnaire et expert en téléphonie Cloud Asterisk, il guide BZ Telecom vers l'excellence avec un service humain inclus qui fait toute la différence. Il voit PRIsME comme l'outil qui structure et documente l'excellence BZ Telecom.",
      attentes: "Martin s'attend à ce que PRIsME reflète l'excellence BZ Telecom et facilite la gouvernance du partenariat stratégique avec BZ. Il veut voir chaque processus documenté, chaque KPI suivi, chaque objectif mesuré. Son objectif : que PRIsME soit l'outil qui démontre le professionnalisme et la structure de BZ Telecom.",
      pagePRIsME: "La page PRIsME de BZ Telecom reflète notre excellence : téléphonie Cloud Asterisk, service client 24/7, infrastructure redondante, formation, gouvernance RACI, KPIs, roadmap 2026, pricing rigide, stratégies marketing et ventes. C'est notre vitrine d'excellence télécom qui rassure et inspire confiance."
    }
  ];

  // Organiser par catégorie
  const categories = Array.from(new Set(bzTelecomData.map(item => item.category)));

  return (
    <div className="min-h-screen bg-linear-to-br from-gray-50 to-gray-100">
      <Header 
        title="BZ Telecom" 
        subtitle="Téléphonie Cloud Asterisk avec service humain inclus - Excellence télécom BZ Inc."
      />
      
      <div className="max-w-7xl mx-auto px-8 pb-12">
        <LeadersSection leaders={leaders} title="Notre Leader - BZ Telecom" />
        
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-orange-500"
          >
            <p className="text-sm text-gray-600 mb-1">Services Disponibles</p>
            <p className="text-3xl font-bold text-gray-900">{bzTelecomData.length}</p>
            <p className="text-xs text-gray-500 mt-1">Piliers d'excellence</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-orange-500"
          >
            <p className="text-sm text-gray-600 mb-1">Score Maturité</p>
            <p className="text-3xl font-bold text-gray-900">
              {Math.round(bzTelecomData.reduce((sum, item) => sum + (item.kpiScore || 0), 0) / bzTelecomData.length)}%
            </p>
            <p className="text-xs text-gray-500 mt-1">Moyenne excellence</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500"
          >
            <p className="text-sm text-gray-600 mb-1">Catégories</p>
            <p className="text-3xl font-bold text-gray-900">{categories.length}</p>
            <p className="text-xs text-gray-500 mt-1">Domaines couverts</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-500"
          >
            <p className="text-sm text-gray-600 mb-1">Objectif MRR 2026</p>
            <p className="text-3xl font-bold text-gray-900">+8k$</p>
            <p className="text-xs text-gray-500 mt-1">Par mois (déc. 2026)</p>
          </motion.div>
        </div>

        {/* Cards par catégorie */}
        {categories.map((category, categoryIndex) => {
          const itemsInCategory = bzTelecomData.filter(item => item.category === category);
          
          return (
            <div key={category} className="mb-12">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: categoryIndex * 0.1 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="flex-1 h-px bg-linear-to-r from-orange-500 to-orange-600"></div>
                <h2 className="text-2xl font-bold text-gray-800 px-4">{category}</h2>
                <div className="flex-1 h-px bg-linear-to-l from-orange-500 to-orange-600"></div>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {itemsInCategory.map((item, idx) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: categoryIndex * 0.1 + idx * 0.05 }}
                    className={itemsInCategory.length === 1 ? 'lg:col-start-2' : undefined}
                  >
                    <AikupCard
                      item={item}
                      onClick={() => handleCardClick(item)}
                      onExpertClick={(expert: 'dany' | 'yannick' | 'francois') => {
                        setExpertVisionModal({
                          isOpen: true,
                          expert,
                          item,
                        });
                      }}
                      customLeaders={item.id === 20 ? [
                        { name: 'Félix Bourassa', avatar: '/FB.png', title: 'Félix Bourassa - Exécution commerciale' },
                        { name: 'Martin Carrier', avatar: '/MC.png', title: 'Martin Carrier - Validation & arbitrages' },
                      ] : undefined}
                      index={idx}
                    />
                  </motion.div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Cards Formation et Arbre PRIsME */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <button
            onClick={() => setIsBZTelecomTrainingOpen(true)}
            className="relative isolate h-72 w-full overflow-hidden rounded-lg bg-slate-950 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group border-2 border-cyan-400/40 hover:border-cyan-300 text-left"
          >
            <div
              className="absolute inset-0 z-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
              style={{
                backgroundImage:
                  'url(https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200&auto=format&fit=crop&q=80)',
              }}
            />
            <div className="absolute inset-0 z-0 bg-linear-to-br from-slate-950/95 via-cyan-950/80 to-blue-900/70 group-hover:from-slate-950/90 transition-all duration-300" />

            <div className="relative z-[1] flex h-full flex-col justify-between p-6 text-white">
              <div>
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center shadow-inner group-hover:bg-white/25 transition-colors">
                    <PhoneCall className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex items-center gap-1.5 rounded-full border border-cyan-200/50 bg-cyan-400/20 px-3 py-1 text-xs font-semibold text-cyan-50">
                    <BookOpenCheck className="h-3.5 w-3.5" />
                    Formation
                  </div>
                </div>

                <h3 className="text-lg font-bold leading-tight drop-shadow">Formation téléphonie BZ Telecom</h3>
                <p className="mt-3 text-sm text-white/90 leading-relaxed drop-shadow">
                  Parcours de vente, téléphonie hébergée, câblage et fibre, wiki vulgarisé, offre BZ et produits
                  complémentaires.
                </p>
              </div>

              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-semibold text-cyan-100">PDF source · 11 pages</span>
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-lg leading-none group-hover:translate-x-0.5 transition-transform">
                  →
                </span>
              </div>
            </div>

            <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
          </button>

          <FormationGestionDocumentaireCard
            department="BZ Telecom"
            domain="Télécom"
            index={1}
          />
          <ArbrePRIsMECard
            index={2}
          />

          {/* Card Gestion Documentaire PRIsME - BZ Telecom */}
          <button
            onClick={() => navigate('/gestion-documentaire?domain=TI&asset=bz-telecom-network')}
            className="relative overflow-hidden rounded-3xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer h-64 group border border-orange-200"
          >
            {/* Image de fond */}
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
              style={{
                backgroundImage:
                  'url(https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&auto=format&fit=crop&q=80)',
              }}
            />

            {/* Overlay gradient pour lisibilité */}
            <div className="absolute inset-0 bg-linear-to-t from-black/85 via-black/55 to-black/20 group-hover:from-orange-900/95 group-hover:via-orange-700/80 transition-all duration-300" />

            {/* Contenu */}
            <div className="relative h-full p-6 flex flex-col justify-end text-white">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-2xl bg-white/15 flex items-center justify-center shadow-inner">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-bold drop-shadow">Gestion Documentaire PRIsME – BZ Telecom</h3>
              </div>
              <p className="text-xs text-white/90 leading-relaxed drop-shadow mb-1">
                Plans stratégiques, matrices RACI, roadmaps, KPIs, tous générés avec PRIsME + IA puis validés par
                l&apos;équipe BZ Telecom.
              </p>
              <span className="inline-flex items-center gap-2 text-[11px] font-semibold text-orange-100 mt-1">
                Explorer les plans & stratégies
                <span className="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center text-[10px] group-hover:translate-x-0.5 transition-transform">
                  →
                </span>
              </span>
            </div>
          </button>
        </div>
      </div>

      <AikupModal
        item={selectedItem as any}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />

      <BZTelecomModal
        isOpen={isBZTelecomModalOpen}
        onClose={() => setIsBZTelecomModalOpen(false)}
      />

      <GuideVenteBZTelecomModal
        isOpen={isGuideVenteModalOpen}
        onClose={() => setIsGuideVenteModalOpen(false)}
      />

      <GouvernanceBZTelecomModal
        isOpen={isGouvernanceModalOpen}
        onClose={() => setIsGouvernanceModalOpen(false)}
      />

      <ComiteTIPrismeModal
        isOpen={isReunionBZTelecomOpen}
        onClose={() => setIsReunionBZTelecomOpen(false)}
        title="Réunions BZ Telecom"
        scope="BZ_TELECOM"
        defaultReunionType="Comité BZ Telecom"
      />

      <ExpertVisionModal
        isOpen={expertVisionModal.isOpen}
        onClose={() => setExpertVisionModal({ isOpen: false, expert: null, item: null })}
        expert={expertVisionModal.expert}
        item={expertVisionModal.item}
      />

      <FelixActionPlanModal
        isOpen={isFelixActionPlanModalOpen}
        onClose={() => setIsFelixActionPlanModalOpen(false)}
      />

      <ResponsabiliteVentesBZTelecomModal
        isOpen={isResponsabiliteVentesOpen}
        onClose={() => setIsResponsabiliteVentesOpen(false)}
      />

      <FormationTelephonieBZTelecomModal
        isOpen={isBZTelecomTrainingOpen}
        onClose={() => setIsBZTelecomTrainingOpen(false)}
      />

      <AIInsightBubble />
    </div>
  );
}
